import globalLogger from "@lazarentertainment/js-logger"
import Cookies from "js-cookie"
import { isSafari } from "react-device-detect"

const ACCESS_TOKEN_KEY = "s10_access_token"
const REFRESH_TOKEN_KEY = "s10_refresh_token"

export const isEmbedded =
  typeof window !== "undefined" && window !== window.parent

export const getAccessToken = (): string | null => {
  try {
    if (typeof window === "undefined") return null
    const token = Cookies.get(ACCESS_TOKEN_KEY)
    if (token) return token
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  } catch (err: unknown) {
    globalLogger.error("Unable to get access token:", err)
    return null
  }
}

export const getRefreshToken = (): string | null => {
  try {
    if (typeof window === "undefined") return null
    const token = Cookies.get(REFRESH_TOKEN_KEY)
    if (token) return token
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  } catch (err: unknown) {
    globalLogger.error("Unable to get refresh token:", err)
    return null
  }
}

export const storeAccessToken = (token: string): void => {
  try {
    if (typeof window === "undefined") return
    if (isSafari && isEmbedded) {
      localStorage.setItem(ACCESS_TOKEN_KEY, token)
    } else {
      Cookies.set(ACCESS_TOKEN_KEY, token, {
        domain: window.location.host,
        sameSite: "none",
        secure: true,
        expires: 30, // days
      })
    }
  } catch (err: unknown) {
    globalLogger.error("Unable to store access token:", err)
  }
}

export const storeRefreshToken = (token: string): void => {
  try {
    if (typeof window === "undefined") return
    if (isSafari && isEmbedded) {
      localStorage.setItem(REFRESH_TOKEN_KEY, token)
    } else {
      Cookies.set(REFRESH_TOKEN_KEY, token, {
        domain: window.location.host,
        sameSite: "none",
        secure: true,
        expires: 30, // days
      })
    }
  } catch (err: unknown) {
    globalLogger.error("Unable to store refresh token:", err)
  }
}

export const clearAccessToken = (): void => {
  try {
    if (typeof window === "undefined") return
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    Cookies.remove(ACCESS_TOKEN_KEY, {
      domain: window.location.host,
      secure: true,
      sameSite: "none",
    })
  } catch (err: unknown) {
    globalLogger.error("Unable to clear access token:", err)
  }
}

export const clearRefreshToken = (): void => {
  try {
    if (typeof window === "undefined") return
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    Cookies.remove(REFRESH_TOKEN_KEY, {
      domain: window.location.host,
      secure: true,
      sameSite: "none",
    })
  } catch (err: unknown) {
    globalLogger.error("Unable to clear refresh token:", err)
  }
}

export const clearTokens = (): void => {
  try {
    if (typeof window === "undefined") return
    clearAccessToken()
    clearRefreshToken()
    window.location.reload()
  } catch (err: unknown) {
    globalLogger.error("Unable to clear refresh token:", err)
  }
}

export const refreshTokens = async ({
  tokenRefreshUri,
}: {
  tokenRefreshUri: string
}): Promise<{
  accessToken: string
  refreshToken: string
}> => {
  const tokens = await fetch(tokenRefreshUri, { method: "POST" })
    .then(async (res) => {
      const body = await res.json()
      if (res.ok) {
        return body
      }
      throw new Error(body.error)
    })
    .catch((err) => {
      globalLogger.error("Failed to refresh tokens:", err)
      throw err
    })

  // Update cookies
  storeAccessToken(tokens.accessToken)
  storeRefreshToken(tokens.refreshToken)

  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
  }
}
